'use client';
import React, { useRef, useEffect, useMemo } from 'react';

import { useTranslations } from 'next-intl';
import {
  useIsAuthenticated,
  useIsAuthenticating,
} from '@wwe-portal/web/providers/stores/auth-store';
import { useGetCurrentUser } from '@wwe-portal/web/providers/queries/user-info';
import {
  useGetConsolidatedOffers,
  useGetUserStocks,
} from '@wwe-portal/web/providers/queries/offer';

import WebCoinShopBanner from '../offer-card-banner/offer-card-banner';
import { WebCoinNavigation } from './web-coin-navigation';
import { useGetWebcoins } from '@wwe-portal/web/providers/queries/currency';
import {
  useInvalidateLiveFramesAndAvatars,
  useGetLiveFramesAndAvatars,
} from '@wwe-portal/web/providers/queries/webcoin';
import { LiveFramesAndAvatar } from '@wwe-portal/entities/webcoin.type';
import usePurchaseByWebCoins from '@wwe-portal/web/services/payment/usePurchaseByWebcoins';
import { NotEnoughWebCoinDialog } from '../club-store/modals/NotEnoughWebCoinDialog';
import { showDialog } from '@wwe-portal/ui/components/dialog';
import usePersistentOffer from '@wwe-portal/web/services/payment/usePersistentOffer';
import { AlternativePurchaseTypes } from '@wwe-portal/web/services/payment/type';
import { LoginDialogPortable } from '../auth/login-dialog';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { VERIFY_DURATION_MS } from '@wwe-portal/web/utils/constants/offer';
import WebCoinContent from './web-coin-content';
import { getStockQuantity } from '@wwe-portal/web/utils/offer/mapper';
import { useProcessingDialog } from '@wwe-portal/web/hooks/payment/processing-dialog';
import { WebCoinSkeleton } from './web-coin-skeleton';
import { purchaseWebCoinAnalytics } from '@wwe-portal/web/libs/analytics/web-coin-shop';
import { extractSuperstarName } from '@wwe-portal/web/utils/superstar-details/extractor';
import { useInvalidateOffer } from '@wwe-portal/web/hooks/payment/use-invalidate-offer';
import { NotSufficientWebcoinsError } from '@wwe-portal/web/services/payment/exception';
import { useToast } from '@wwe-portal/ui/components/toast';

export default function WebCoinMain() {
  const toast = useToast();
  const t = useTranslations();
  const webCoinPlayerAvatarRef = useRef<HTMLDivElement | null>(null);
  const dailyWebCoinOfferRef = useRef<HTMLDivElement | null>(null);
  const avatarFramesRef = useRef<HTMLDivElement | null>(null);
  const energyPacksRef = useRef<HTMLDivElement | null>(null);

  const isAuthenticated = useIsAuthenticated();
  const isAuthenticating = useIsAuthenticating();
  const currentUser = useGetCurrentUser();
  const {
    data: LiveFramesAndAvatarsData,
    isLoading: isLoadingLiveFramesAndAvatars,
  } = useGetLiveFramesAndAvatars();
  const {
    pacificDate,
    avatars: avatarsList = [],
    frames: framesList = [],
    energyPacks: energyPackageList = [],
  } = LiveFramesAndAvatarsData?.data.data ?? ({} as LiveFramesAndAvatar);

  const { data: consolidatedData, isLoading: isLoadingWebcoinDaily } =
    useGetConsolidatedOffers(['webCoinDaily']);
  const webcoinDailyCategory = consolidatedData?.webCoinDaily;

  const offers = useMemo(() => {
    if (!webcoinDailyCategory) return [];
    return webcoinDailyCategory.flatMap((section) => section.offers);
  }, [webcoinDailyCategory]);

  const invalidateLiveFramesAndAvatars = useInvalidateLiveFramesAndAvatars();
  const { stockMap, isLoading: isStockMapLoading } = useGetUserStocks();

  const isLoading =
    isAuthenticating ||
    isLoadingWebcoinDaily ||
    isStockMapLoading ||
    isLoadingLiveFramesAndAvatars;

  const { amount: currentWebCoin } = useGetWebcoins();
  const invalidateOffer = useInvalidateOffer();

  const mappedStockDailyOffers = useMemo(() => {
    if (isLoading || !offers) return [];
    return offers.map((offer: OfferData) => {
      const stockQuantity = getStockQuantity(offer, {
        stockMap,
        isAuthenticated,
      });
      return {
        ...offer,
        StockQuantity: stockQuantity,
      };
    });
  }, [isLoading, stockMap, offers, isAuthenticated]);

  const webCoinDailyOffers = isAuthenticated
    ? mappedStockDailyOffers.filter(
        (offer: OfferData) => offer.PortalAvailability
      )
    : mappedStockDailyOffers;

  const {
    persistPurchasingOffer,
    clearPurchasingOffer,
    isPaymentSuccess,
    purchasingOffer,
  } = usePersistentOffer();

  const getDialogMessages = () => {
    if (
      purchasingOffer?.alternativePurchaseType ===
      AlternativePurchaseTypes.Avatar
    ) {
      return {
        processTitle: t('webCoin_avatar_title_load'),
        processDescription: t('webCoin_avatar_description_load'),
        successTitle: t('webCoin_avatar_title_successful'),
        successDescription: t('webCoin_avatar_description_successful'),
      };
    }
    if (
      purchasingOffer?.alternativePurchaseType ===
      AlternativePurchaseTypes.Frame
    ) {
      return {
        processTitle: t('webCoin_frame_title_load'),
        processDescription: t('webCoin_frame_description_load'),
        successTitle: t('webCoin_frame_title_successful'),
        successDescription: t('webCoin_frame_description_successful'),
      };
    }
    if (
      purchasingOffer?.alternativePurchaseType ===
      AlternativePurchaseTypes.Energy
    ) {
      return {
        processTitle: t('webCoin_energy_title_load'),
        processDescription: t('webCoin_energy_description_load'),
        successTitle: t('webCoin_energy_title_successful'),
        successDescription: t('webCoin_energy_description_successful'),
      };
    }
    return {
      processTitle: t('webCoin_daily_title_load'),
      processDescription: t('webCoin_daily_description_load'),
      successTitle: t('webCoin_daily_title_successful'),
      successDescription: t('webCoin_daily_description_successful'),
    };
  };

  const { showProcess, showSuccess, modal } = useProcessingDialog({
    progressData: {
      duration: VERIFY_DURATION_MS,
      title: getDialogMessages().processTitle,
      description: getDialogMessages().processDescription,
    },
    successData: {
      title: getDialogMessages().successTitle,
      description: getDialogMessages().successDescription,
    },
    onClose: () => {
      clearPurchasingOffer();
    },
  });

  const { purchaseItemsByWebCoins } = usePurchaseByWebCoins({
    persistPurchasingOffer,
    onError: (error) => {
      clearPurchasingOffer();

      if (error instanceof NotSufficientWebcoinsError) {
        showDialog(NotEnoughWebCoinDialog);
        return;
      }

      toast.toast({
        content: t('something_went_wrong'),
        variant: 'error',
      });
    },
    onPaymentSuccess: async (_, { offerData, count }) => {
      const itemBalance =
        (currentWebCoin ?? 0) - Number(offerData.AlternativeCostAmount);

      showProcess();
      await Promise.all([invalidateLiveFramesAndAvatars(), invalidateOffer()]);

      modal.remove();
      showSuccess();

      purchaseWebCoinAnalytics({
        currentUser,
        offerData,
        itemBalance,
        count,
      });
    },
  });

  const handleItemsPurchase = ({
    price,
    itemId,
    type,
    count,
    title,
  }: {
    itemId: number | string | undefined;
    price: number;
    type: AlternativePurchaseTypes;
    count?: number;
    title: string;
  }) => {
    if (isAuthenticated && currentUser) {
      purchaseItemsByWebCoins({
        offerData: {
          OffersID: itemId,
          AlternativeCostAmount: price,
          OfferType: 'AlternativeCurrency',
          title,
          offerItems: [],
          factionGiftItems: [],
        } as OfferData,
        alternativePurchaseType: type,
        count,
      });
    } else {
      showDialog(LoginDialogPortable, {
        title: 'Login To Make A Purchase',
      });
    }
  };

  /**
   *  Handle Daily Web Coin Offer Purchase
   */
  const handlePurchaseDailyWebCoinOffer = async (offer) => {
    if (isAuthenticated && currentUser) {
      purchaseItemsByWebCoins({
        offerData: offer,
        alternativePurchaseType: AlternativePurchaseTypes.Daily,
      });
    } else {
      showDialog(LoginDialogPortable, {
        title: 'Login To Make A Purchase',
      });
    }
  };

  const handleUpdateLiveList = () => {
    invalidateLiveFramesAndAvatars();
  };

  useEffect(() => {
    handleUpdateLiveList();
  }, [isAuthenticated]);

  /**
   *  Handle Web Coin avatar card title
   */
  const handleWebCoinAvatarCardTitle = (title: string) => {
    if (!title || title === '') return 'Avatar';
    const subsObj = extractSuperstarName(title);

    return typeof subsObj === 'object' && subsObj.mainString
      ? subsObj.mainString
      : '';
  };
  const framesListSorted = framesList.sort((a, b) => a.price - b.price);
  const avatarsListSorted = avatarsList.sort((a, b) => a.price - b.price);

  if (isLoading) return <WebCoinSkeleton />;
  return (
    <div>
      <div className="text-text300 text-xl font-semibold">Web Coin Shop</div>
      <WebCoinNavigation />
      <WebCoinShopBanner />
      <WebCoinContent
        webCoinPlayerAvatarRef={webCoinPlayerAvatarRef}
        pacificDate={pacificDate}
        handleUpdateLiveList={handleUpdateLiveList}
        avatarsList={avatarsListSorted}
        handleWebCoinAvatarCardTitle={handleWebCoinAvatarCardTitle}
        handleItemsPurchase={handleItemsPurchase}
        purchasingOffer={purchasingOffer}
        avatarFramesRef={avatarFramesRef}
        framesList={framesListSorted}
        dailyWebCoinOfferRef={dailyWebCoinOfferRef}
        webCoinDailyOffers={webCoinDailyOffers}
        handlePurchaseDailyWebCoinOffer={handlePurchaseDailyWebCoinOffer}
        energyPackageList={energyPackageList}
        energyPacksRef={energyPacksRef}
        isPaymentSuccess={isPaymentSuccess}
      />
    </div>
  );
}
